import { useEffect, useRef } from 'react';
import { FrameWatch } from '../frameWatch';
import { getScene, subscribeScene, type Scene } from '../scene';
import { compile, softwareRendered } from '../webgl';

/*
 * The sea behind the game: sunlit shallows over sand, with the moving light of caustics.
 * An original WebGL shader written for PAWNED (see CREDITS.md): two layers of animated Voronoi
 * cells whose borders make the bright lines, gently warped so they ripple. It renders at a
 * fraction of the screen resolution (the light is soft anyway), at most 30 frames a second,
 * with fewer pixels on a device that cannot keep up (src/ui/frameWatch.ts), not at all while
 * the page is hidden, and as one still frame when motion is reduced, when the graphics are drawn
 * by the processor, or when even the fewest pixels are too many. Without WebGL the page keeps
 * its CSS gradient.
 */

const VERTEX = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

const FRAGMENT = `
precision mediump float;
uniform vec2 u_res;
uniform float u_time;
uniform float u_cell;
// The Long Night (docs/LONG_NIGHT_ART_DIRECTION.md §13): how far down the look has gone (0 the
// sunlit surface, 1 the trench), sunrise at dawn, and the grey of After Hours.
uniform float u_depth;
uniform float u_dawn;
uniform float u_grey;

vec2 hash2(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453);
}

// How close p is to a border between the cells of a slowly moving Voronoi pattern (0 on it).
float border(vec2 p, float t) {
  vec2 n = floor(p);
  vec2 f = fract(p);
  float d1 = 8.0;
  float d2 = 8.0;
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 g = vec2(float(i), float(j));
      vec2 o = hash2(n + g);
      o = 0.5 + 0.42 * sin(t + 6.2831 * o);
      vec2 r = g + o - f;
      float d = dot(r, r);
      if (d < d1) { d2 = d1; d1 = d; }
      else if (d < d2) { d2 = d; }
    }
  }
  return sqrt(d2) - sqrt(d1);
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_res;
  vec2 p = gl_FragCoord.xy / u_cell;
  float t = u_time;
  p += vec2(t * 0.021, t * 0.014);
  p += 0.14 * vec2(sin(p.y * 1.7 + t * 0.35), cos(p.x * 1.3 - t * 0.3));
  float a = 1.0 - smoothstep(0.0, 0.13, border(p, t * 0.32));
  float b = 1.0 - smoothstep(0.0, 0.09, border(p * 1.63 + 7.3, -t * 0.27));
  float light = a * a * 0.62 + b * b * 0.34;

  vec3 sand = vec3(0.992, 0.957, 0.875);
  vec3 shallows = vec3(0.866, 0.941, 0.925);
  vec3 deep = vec3(0.741, 0.882, 0.878);
  vec3 col = mix(sand, shallows, smoothstep(0.0, 0.62, uv.y));
  col = mix(col, deep, smoothstep(0.55, 1.0, uv.y) * 0.55);
  col += vec3(1.0, 0.99, 0.94) * light * 0.11;
  float edge = smoothstep(0.42, 1.05, length((uv - vec2(0.5, 0.52)) * vec2(1.0, 0.9)));
  col = mix(col, vec3(0.60, 0.80, 0.80), edge * 0.24);

  // Down from the surface into dark water: dusk on the shallows, the blue hour, night on the
  // reef, the deep. The caustics fade as the light goes; in the dark a few specks glow instead.
  vec3 dusk = mix(vec3(0.93, 0.76, 0.68), vec3(0.36, 0.48, 0.66), uv.y);
  vec3 blue = mix(vec3(0.10, 0.20, 0.36), vec3(0.16, 0.30, 0.48), uv.y);
  vec3 reef = mix(vec3(0.02, 0.07, 0.13), vec3(0.05, 0.13, 0.22), uv.y);
  vec3 trench = vec3(0.004, 0.012, 0.024);
  float d = clamp(u_depth, 0.0, 1.0);
  vec3 water = mix(col, dusk, smoothstep(0.0, 0.25, d));
  water = mix(water, blue, smoothstep(0.25, 0.5, d));
  water = mix(water, reef, smoothstep(0.5, 0.75, d));
  water = mix(water, trench, smoothstep(0.75, 1.0, d));
  water += vec3(0.75, 0.92, 1.0) * light * 0.09 * step(0.01, d) * (1.0 - smoothstep(0.4, 0.8, d));
  water += vec3(0.45, 1.0, 0.85) * b * b * b * 0.10 * smoothstep(0.55, 0.8, d) * (1.0 - u_grey);
  // Dawn: sunrise on the water, lit from below.
  water = mix(water, mix(vec3(1.0, 0.74, 0.56), vec3(0.62, 0.70, 0.90), uv.y), u_dawn * 0.85);
  // After Hours: night water with no moon.
  float g = dot(water, vec3(0.3, 0.59, 0.11));
  water = mix(water, vec3(g * 0.8), u_grey);
  // Flat bands of color, as pixel art has.
  water = floor(water * 28.0 + 0.5) / 28.0;
  gl_FragColor = vec4(water, 1.0);
}
`;

/**
 * Where the scene puts the sea (§13): how deep, whether dawn, how grey. Outside the Long Night
 * (the void's default scene, unlit) it is the sunlit surface.
 */
export function seaOf(scene: Scene): { depth: number; dawn: number; grey: number } {
  const grey = scene.smoke === 'afterHours' ? 1 : scene.desaturate;
  switch (scene.smoke) {
    case 'void':
      return { depth: scene.lamp > 0 ? 0.22 : 0, dawn: 0, grey };
    case 'night2':
      return { depth: 0.45, dawn: 0, grey };
    case 'night3':
      return { depth: 0.68, dawn: 0, grey };
    case 'closing':
      return { depth: 1, dawn: 0, grey };
    case 'backRoom':
      return { depth: 0.85, dawn: 0, grey };
    case 'afterHours':
      return { depth: 0.8, dawn: 0, grey: 1 };
    case 'dawn':
      return { depth: 0.1, dawn: 1, grey: 0 };
    default:
      // A regular's table, deep in the Night.
      return { depth: 0.55, dawn: 0, grey };
  }
}

/** Canvas pixels per CSS pixel: the sea is pixel art, a canvas pixel five CSS pixels wide, scaled
    up without smoothing (theme-sea.css), its colors in flat bands. */
const SCALE = 1 / 5;
/** Size of a caustic cell, in CSS pixels. */
const CELL = 150;
const FRAME_MS = 1000 / 30;
/** A device that cannot keep up gets fewer pixels (src/ui/frameWatch.ts), down to this; below
    it the background holds still. */
const MIN_SCALE = 0.07;
/** The time of the still frame (reduced motion): chosen for a pleasant pattern. */
const STILL_TIME = 37;

export function SeaBackground({ still: stillProp }: { still: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let still = stillProp;
    const canvas = ref.current;
    if (!canvas) return;
    let gl: WebGLRenderingContext | null;
    try {
      gl = canvas.getContext('webgl', {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        premultipliedAlpha: false,
        powerPreference: 'low-power',
      });
    } catch {
      gl = null;
    }
    if (!gl) return;
    if (softwareRendered(gl)) still = true;
    const vs = compile(gl, gl.VERTEX_SHADER, VERTEX);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
    const program = gl.createProgram();
    if (!vs || !fs || !program) return;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    gl.useProgram(program);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    // One triangle that covers the screen.
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const pos = gl.getAttribLocation(program, 'a_pos');
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);
    const uRes = gl.getUniformLocation(program, 'u_res');
    const uTime = gl.getUniformLocation(program, 'u_time');
    const uCell = gl.getUniformLocation(program, 'u_cell');
    const uDepth = gl.getUniformLocation(program, 'u_depth');
    const uDawn = gl.getUniformLocation(program, 'u_dawn');
    const uGrey = gl.getUniformLocation(program, 'u_grey');
    // The sea never cuts either: a change of scene eases in over about 2.4 s.
    const target = seaOf(getScene());
    const shown = { ...target };
    const setSea = () => {
      gl!.uniform1f(uDepth, shown.depth);
      gl!.uniform1f(uDawn, shown.dawn);
      gl!.uniform1f(uGrey, shown.grey);
    };
    setSea();

    const context = gl;
    let frame = 0;
    let last = 0;
    let scale = SCALE;
    const watch = new FrameWatch();
    const start = performance.now();
    const draw = (time: number) => {
      // Ease toward where the scene puts the sea.
      for (const k of ['depth', 'dawn', 'grey'] as const)
        shown[k] += (target[k] - shown[k]) * (still ? 1 : 0.05);
      setSea();
      context.uniform1f(uTime, time);
      context.drawArrays(context.TRIANGLES, 0, 3);
      canvas.classList.add('is-ready');
    };
    const resize = () => {
      const w = Math.max(1, Math.round(canvas.clientWidth * scale));
      const h = Math.max(1, Math.round(canvas.clientHeight * scale));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        context.viewport(0, 0, w, h);
      }
      context.uniform2f(uRes, w, h);
      context.uniform1f(uCell, CELL * scale);
      if (still) draw(STILL_TIME);
    };
    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      // Frames keep coming late: fewer pixels, and in the end a still picture (the last frame
      // drawn stays on the canvas).
      if (watch.struggling(now)) {
        scale *= 0.7;
        if (scale < MIN_SCALE) {
          still = true;
          cancelAnimationFrame(frame);
          return;
        }
        // A resized canvas is blank: draw it again straight away.
        resize();
        last = 0;
      }
      if (now - last < FRAME_MS) return;
      last = now;
      draw(STILL_TIME + (now - start) / 1000);
    };
    const onVisibility = () => {
      cancelAnimationFrame(frame);
      watch.reset();
      if (!document.hidden && !still) frame = requestAnimationFrame(tick);
    };
    const unsubscribe = subscribeScene(() => {
      Object.assign(target, seaOf(getScene()));
      if (still) draw(STILL_TIME);
    });
    resize();
    window.addEventListener('resize', resize);
    if (!still) {
      document.addEventListener('visibilitychange', onVisibility);
      frame = requestAnimationFrame(tick);
    }
    const onLost = (e: Event) => {
      e.preventDefault();
      cancelAnimationFrame(frame);
    };
    canvas.addEventListener('webglcontextlost', onLost);
    return () => {
      cancelAnimationFrame(frame);
      unsubscribe();
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
      canvas.removeEventListener('webglcontextlost', onLost);
      context.deleteBuffer(buffer);
      context.deleteProgram(program);
      context.deleteShader(vs);
      context.deleteShader(fs);
    };
  }, [stillProp]);
  return <canvas ref={ref} className="sea-canvas" aria-hidden="true" />;
}
